import { ReactNode, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ConfigurationName } from '../../enums/ConfigurationName';
import useNswagClient from '../../hooks/api/useNswagClient';
import { resolvePreviewSwap } from '../../utils/featureAccess';
import LoadingWheel from '../ui/LoadingWheel';
import PageArea from './PageArea';
import { UserContext } from './useUser';

interface ConfigurationPreviewRouteProps {
  children: ReactNode;
  configurationName: ConfigurationName;
  legacyPath: string;
  previewPath: string;
  version: 'legacy' | 'preview';
}

interface ConfigurationResult {
  enabled: boolean;
  scope: string;
}

interface ConfigurationValueResponse {
  configurations?: unknown;
  value?: string;
}

const ConfigurationPreviewRoute = ({
  children,
  configurationName,
  legacyPath,
  previewPath,
  version,
}: ConfigurationPreviewRouteProps) => {
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { getConfiguration } = useNswagClient();
  const hostname = typeof window === 'undefined' ? undefined : window.location.hostname;
  const hasAdminPermission = hasPermissionTo(['Admin']);
  // Only test hostnames skip the configuration fetch here - Admins still need the fetched
  // value below, since an enabled configuration must resolve to 'preview' even for Admins.
  const previewSwapMode = resolvePreviewSwap(hostname, false);
  const configurationScope = `${selectedStore?.franchiseName ?? ''}:${selectedStore?.storeNumber ?? ''}`;
  const [configurationResult, setConfigurationResult] = useState<ConfigurationResult | null>(null);

  useEffect(() => {
    if (previewSwapMode === 'both') return;

    let isCurrent = true;
    getConfiguration(
      configurationName,
      selectedStore?.storeNumber ?? '',
      selectedStore?.franchiseName ?? '',
    )
      .then((response) => {
        if (isCurrent) {
          const configuration = response?.data as ConfigurationValueResponse | undefined;
          setConfigurationResult({
            enabled: configuration?.value === 'true',
            scope: configurationScope,
          });
        }
      })
      .catch(() => {
        if (isCurrent) {
          setConfigurationResult({ enabled: false, scope: configurationScope });
        }
      });

    return () => {
      isCurrent = false;
    };
    // getConfiguration is recreated on each render by useNswagClient.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationName, configurationScope, previewSwapMode]);

  if (previewSwapMode === 'both') return <>{children}</>;

  if (configurationResult?.scope !== configurationScope) {
    return (
      <PageArea>
        <LoadingWheel />
      </PageArea>
    );
  }

  const resolvedMode = resolvePreviewSwap(hostname, configurationResult.enabled, hasAdminPermission);
  // 'both' (e.g. Admin showcase) means neither version should force-redirect the other.
  if (resolvedMode !== 'both' && resolvedMode !== version) {
    return (
      <Navigate
        to={resolvedMode === 'preview' ? previewPath : legacyPath}
        replace />
    );
  }

  return <>{children}</>;
};

export default ConfigurationPreviewRoute;